'use client'

import { useState, useEffect, useMemo } from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import { CalendarView } from '@/components/calendar/calendar-view'
import { PageHeader } from '@/components/layout/page-header'
import { useTranslations } from '@/lib/i18n'
import { useProjects } from '@/hooks/use-common-api'

interface Project {
  id: number
  name: string
  identifier: string
  description?: string
  status: number
}

interface CalendarPageProps {
  projectId?: string  // If provided, it's a project calendar; otherwise it's the global calendar
  showProjectSelector?: boolean  // Show project dropdown selector
  isEmbedded?: boolean  // If true, don't add extra padding (used when embedded in project pages)
}

/**
 * Unified Calendar Page Component
 * Used across all calendar pages in the application
 * - /calendar - Global calendar with project selector
 * - /projects/[id]/calendar - Project-specific calendar
 */
export function CalendarPage({ projectId, showProjectSelector = true, isEmbedded = false }: CalendarPageProps) {
  const t = useTranslations('calendarPage')

  // Use React Query hook for cached projects data
  const { data: projectsData } = useProjects(1000)
  const projects = useMemo(() => projectsData?.projects || [], [projectsData])

  const [selectedProjectId, setSelectedProjectId] = useState<string>(projectId || '')

  // Update selected project if prop changes (for project-specific calendar)
  useEffect(() => {
    if (projectId) {
      setSelectedProjectId(projectId)
    }
  }, [projectId])

  const handleProjectChange = (value: string) => {
    setSelectedProjectId(value)
  }

  const selectedProject = projects.find(p => p.id.toString() === selectedProjectId)

  const wrapperClasses = isEmbedded
    ? ''
    : 'min-h-screen bg-background'
  const containerClasses = isEmbedded
    ? ''
    : 'w-full px-4 sm:px-6 lg:px-8 py-8'

  const heroSubtitle = selectedProject
    ? t('projectTasksDescription', { projectName: selectedProject.name })
    : selectedProjectId
      ? t('subtitle')
      : t('selectProjectDescription')

  return (
    <div className={wrapperClasses}>
      <div className={containerClasses}>
        {!isEmbedded && (
          <PageHeader
            icon={CalendarIcon}
            illustration="calendar"
            title={t('title')}
            subtitle={heroSubtitle}
          />
        )}
        <CalendarView
          key={selectedProjectId} // Force re-render when project changes
          projectId={selectedProjectId || undefined}
          title={isEmbedded ? t('title') : ''}
          description={isEmbedded ? heroSubtitle : ''}
          showProjectSelector={showProjectSelector && !projectId}
          projects={projects}
          selectedProjectId={selectedProjectId}
          onProjectChange={handleProjectChange}
        />
      </div>
    </div>
  )
}
